import type { StateMachine } from './machine';

/**
 * Statut d'un PAIEMENT (une transaction : Stripe, espèces, chèque…), cahier des charges §10.
 *
 * Règles absolues :
 *  - un paiement Stripe ne devient `succeeded` / `failed` / `refunded` / `disputed` que sur
 *    webhook serveur à signature vérifiée (acteur `stripe_webhook`), jamais depuis un client ;
 *  - les espèces (et tout moyen réglé « à remettre », ex. chèque) restent « à remettre »
 *    (`to_remit`) jusqu'à confirmation par une personne détenant la permission fine
 *    `payment.cash.confirm` (retirable individuellement) ;
 *  - un virement / autre moyen hors Stripe reste « en attente » jusqu'à ce que sa réception soit
 *    constatée par cette même permission : le collecteur ne valide jamais son propre encaissement.
 */
export const PAYMENT_STATUSES = [
  'pending',
  'processing',
  'to_remit',
  'succeeded',
  'failed',
  'canceled',
  'partially_refunded',
  'refunded',
  'disputed',
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

const WEBHOOK = ['stripe_webhook'] as const;

export const paymentMachine: StateMachine<PaymentStatus> = {
  name: 'payment',
  states: PAYMENT_STATUSES,
  initial: 'pending',
  entryStates: ['to_remit'],
  terminal: ['canceled', 'refunded'],
  labels: {
    pending: 'En attente',
    processing: 'En cours',
    to_remit: 'Espèces à remettre',
    succeeded: 'Encaissé',
    failed: 'Échoué',
    canceled: 'Annulé',
    partially_refunded: 'Partiellement remboursé',
    refunded: 'Remboursé',
    disputed: 'Litige',
  },
  transitions: [
    // Stripe
    { from: ['pending'], to: 'processing', permission: null, actors: WEBHOOK },
    { from: ['pending', 'processing'], to: 'succeeded', permission: null, actors: WEBHOOK },
    { from: ['pending', 'processing'], to: 'failed', permission: null, actors: WEBHOOK },
    { from: ['succeeded'], to: 'partially_refunded', permission: null, actors: WEBHOOK },
    { from: ['succeeded', 'partially_refunded'], to: 'refunded', permission: null, actors: WEBHOOK },
    { from: ['succeeded', 'partially_refunded'], to: 'disputed', permission: null, actors: WEBHOOK },
    { from: ['disputed'], to: 'succeeded', permission: null, actors: WEBHOOK },
    { from: ['disputed'], to: 'refunded', permission: null, actors: WEBHOOK },
    // Espèces / chèque (moyen « à remettre ») : remise confirmée par un admin/superviseur autorisé
    { from: ['to_remit'], to: 'succeeded', permission: 'payment.cash.confirm', actors: ['user'] },
    // Virement / autre moyen hors Stripe : réception constatée par une personne autorisée
    { from: ['pending'], to: 'succeeded', permission: 'payment.cash.confirm', actors: ['user'] },
    // Stripe : nouveau lien après échec / expiration
    { from: ['failed'], to: 'pending', permission: 'payment.collect', actors: ['user', 'system'] },
    { from: ['pending', 'to_remit', 'failed'], to: 'canceled', permission: 'payment.collect', reasonCategory: 'payment_cancel', actors: ['user'] },
    // Remboursement hors Stripe (espèces rendues, chèque retourné…) : commentaire obligatoire ;
    // un remboursement partiel supplémentaire met à jour le montant sans changer de statut.
    // Stripe : le serveur refuse ces transitions manuelles (remboursement via l'API, puis webhook).
    { from: ['succeeded'], to: 'partially_refunded', permission: 'payment.refund', requiresComment: true, actors: ['user'] },
    { from: ['succeeded', 'partially_refunded'], to: 'refunded', permission: 'payment.refund', requiresComment: true, actors: ['user'] },
  ],
};

/**
 * Statut de paiement AFFICHÉ sur un dossier (§10 : À payer, Partiellement payé, Payé, En cours,
 * Remboursé) : il est CALCULÉ à partir des paiements, jamais saisi, et n'a aucun lien avec le
 * statut du dossier.
 */
export const PAYMENT_SUMMARY_STATUSES = ['to_pay', 'partially_paid', 'paid', 'in_progress', 'refunded'] as const;
export type PaymentSummaryStatus = (typeof PAYMENT_SUMMARY_STATUSES)[number];
export const PAYMENT_SUMMARY_LABELS: Record<PaymentSummaryStatus, string> = {
  to_pay: 'À payer',
  partially_paid: 'Partiellement payé',
  paid: 'Payé',
  in_progress: 'En cours',
  refunded: 'Remboursé',
};

/** Amounts in cents. `refundedCents` is part of `amountCents` already refunded. */
export interface PaymentLine {
  status: PaymentStatus;
  amountCents: number;
  refundedCents?: number;
}

export function paymentSummary(totalCents: number, payments: PaymentLine[]) {
  const settled = payments
    .filter((p) => p.status === 'succeeded' || p.status === 'partially_refunded' || p.status === 'disputed')
    .reduce((s, p) => s + p.amountCents - (p.refundedCents ?? 0), 0);
  const inFlight = payments
    .filter((p) => p.status === 'pending' || p.status === 'processing' || p.status === 'to_remit')
    .reduce((s, p) => s + p.amountCents, 0);
  const refunded = payments.filter((p) => p.status === 'refunded').length > 0;
  const balanceCents = Math.max(totalCents - settled, 0);
  let status: PaymentSummaryStatus;
  if (settled >= totalCents && totalCents > 0) status = 'paid';
  else if (inFlight > 0) status = 'in_progress';
  else if (settled > 0) status = 'partially_paid';
  else if (refunded) status = 'refunded';
  else status = 'to_pay';
  return { status, paidCents: settled, pendingCents: inFlight, balanceCents };
}
